import { uploadFileQuery } from "api/UdasWrapper";
import { toast } from "react-toastify";
import { PhoneNumberUtil } from "google-libphonenumber";
import * as Yup from "yup";
import { insertIntegrations } from "api/Integrations";
import { getEnv } from "./environment";
import { LOGO_SIZE_ERROR_MESSAGE } from "constants/portal";
import snappyjs from "snappyjs";

const COMPRESSED_COLUMNS = ["connectors", "credentials", "steps", "flow_ids", "share_user"],
   TEXT_DECODER = new TextDecoder();

const setCookie = (cname, cvalue, exdays) => {
   if (exdays) {
      const d = new Date();
      d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
      let expires = "expires=" + d.toUTCString();
      document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
      return;
   }
   document.cookie = cname + "=" + cvalue + ";path=/";
};

const getCookie = (cname) => {
   let name = cname + "=";
   let ca = document.cookie.split(";");
   for (let i = 0; i < ca?.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") {
         c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
         return c.substring(name.length, c.length);
      }
   }
   return "";
};

const uploadFile = async (user, file, destination, componentKey, preventSuccessMessage) => {
   const replacedString = user?.cxfUserId.replace(/\|/g, "-");
   let filename;
   let response = null;
   let responseMessage;

   if (file.name) {
      filename = replacedString + ".png";
   }

   // Send the FormData object to the `/upload/avatar` endpoint
   try {
      let toUploadData;
      switch (destination) {
         case "flows":
         case "connectors":
         case "credentials":
         case "integrations": {
            filename = `${replacedString}-${componentKey}.png`;
            // falls through
         }
         case "logo":
         case "avatar": {
            const modifiedFile = new File([file], filename, { type: file.type });
            toUploadData = new FormData();
            toUploadData.append(
               "operations",
               JSON.stringify({
                  query: `
        mutation Mutation($file: Upload!, $tenant_id: String!, $type: String!) {
          uploadPublicFile(file: $file, tenant_id: $tenant_id, type: $type) {
            isSuccessful
            message
            resultObject
          }
        }
        `,
                  variables: {
                     file: null,
                     tenant_id: user?.stripeId,
                     type: destination
                  }
               })
            );
            toUploadData.append("map", JSON.stringify({ 0: ["variables.file"] }));
            toUploadData.append("0", modifiedFile);
            const uploadResponse = await uploadFileQuery(toUploadData);
            response = uploadResponse?.uploadPublicFile?.resultObject;
            responseMessage = uploadResponse?.uploadPublicFile?.message;
            break;
         }
         default: {
            const toUploadDataArr = Array.from(file);

            if (toUploadDataArr.length) {
               const formData = new FormData();
               const query = `mutation Mutation($files: [Upload!]!, $componentKey: String!) {
          uploadFileCXFComponents(files: $files, componentKey: $componentKey) {
            isSuccessful
            message
            resultObject
          }
        }`;
               formData.append(
                  "operations",
                  JSON.stringify({
                     query: query,
                     variables: {
                        files: [],
                        componentKey: componentKey
                     }
                  })
               );

               let mapData = {};

               toUploadDataArr.forEach((item, index) => {
                  mapData[`${index}`] = [`variables.files.${index}`];
               });

               formData.append("map", JSON.stringify(mapData));

               toUploadDataArr.forEach((item, index) => {
                  formData.append(`${index}`, item);
               });

               toUploadData = formData;
               const uploadResponse = await uploadFileQuery(toUploadData);
               response = uploadResponse?.uploadFileCXFComponents?.resultObject;
               responseMessage = uploadResponse?.uploadFileCXFComponents?.message;
            }
         }
      }

      if (!preventSuccessMessage) {
         // Display a success message if the upload was successful
         toast.success(responseMessage);
      }

      // Additional success handling logic if needed
   } catch (error) {
      // Display an error message if the upload failed
      toast.error("Faild to upload the file(s)");

      // Additional error handling logic if needed
   }

   return response;
};

const removeDuplicateObjects = (array = [], property) => {
   const uniqueIds = [];
   const unique = array.filter((element) => {
      const isDuplicate = uniqueIds.includes(element[property]);

      if (!isDuplicate) {
         uniqueIds.push(element[property]);
         return true;
      }
      return false;
   });

   return unique;
};

const getLocalTimeString = (time = "") => new Date(time).toLocaleTimeString("en-US");

const getLocalDateTimeString = (date = "") =>
   new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
   });

const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

const stringifyWithSpaces = (obj) => {
   let cache = [];
   let str = JSON.stringify(
      obj,
      function (key, value) {
         if (typeof value === "object" && value !== null) {
            if (cache.indexOf(value) !== -1) {
               // Circular reference found, discard key
               return;
            }
            // Store value in our collection
            cache.push(value);
         }
         return value;
      },
      2
   );
   cache = null; // reset the cache
   return str;
};

const exportToJson = (item, name) => {
   const json = stringifyWithSpaces(item);
   const blob = new Blob([json], { type: "application/json" });
   const url = URL.createObjectURL(blob);
   const a = document.createElement("a");
   a.id = "exportFlowLink";
   a.href = url;

   const fileName = name ? `${name}.json` : item.name?.endsWith(".json") ? item.name : `${item.name || "data"}.json`;
   a.download = fileName;

   a.click();

   URL.revokeObjectURL(url);

   const exportFlowLink = document.getElementById("exportFlowLink");
   if (exportFlowLink) {
      exportFlowLink.remove();
   }
};

const isEmptyValue = (value) => {
   if (typeof value === "string" && value.trim() === "") {
      return true;
   }
   if (Array.isArray(value)) {
      return value.length === 0;
   }
   if (typeof value === "object" && value !== null) {
      return Object.keys(value).length === 0;
   }
   if (typeof value === "number") {
      return false;
   }
   return !value;
};

const getSchemaUrlFromConfigFiles = (configFiles) => {
   const files = configFiles.split(",");
   const schemaUrl = files.find((f) => f.indexOf("schema.json") > -1);

   return schemaUrl;
};

const isArrayofObjects = (arr) => {
   if (!Array.isArray(arr)) {
      return false;
   }

   for (const element of arr) {
      if (typeof element !== "object" || element === null || Array.isArray(element)) {
         return false;
      }
   }

   return true;
};

const removeProperty = (obj, propToRemove) => {
   if (typeof obj !== "object" || obj === null) {
      return obj;
   }

   for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
         const value = obj[key];

         if (key === propToRemove) {
            delete obj[key];
         }

         if (typeof value === "object") {
            removeProperty(value, propToRemove);
         }
      }
   }

   return obj;
};

const getObjectPaths = (obj, currentPath = []) => {
   if (typeof obj !== "object" || obj === null) {
      return [currentPath];
   }

   const paths = [];

   for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
         const value = obj[key];
         const newPath = [...currentPath, key];
         const nestedPaths = getObjectPaths(value, newPath);
         paths.push(...nestedPaths);
      }
   }

   return paths;
};

const deepEqual = (obj1, obj2, fieldsToIgnore) => {
   if (obj1 === obj2) {
      return true;
   }

   if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
      return false;
   }

   let keys1 = Object.keys(obj1);
   let keys2 = Object.keys(obj2);

   if (keys1.length !== keys2.length) {
      return false;
   }

   for (let key of keys1) {
      if (fieldsToIgnore && fieldsToIgnore.includes(key)) {
         continue;
      }

      if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
         return false;
      }
   }

   return true;
};

const isValidJsonString = (str) => {
   try {
      if (!str || (typeof str === "boolean" && str)) {
         return false;
      }
      JSON.parse(str);
      return true;
   } catch (e) {
      return false;
   }
};

const formatDateAndTime = (dateTime = "") => {
   const [date, time] = dateTime.split(", ");
   const formattedDateTime = `${date}\n${time}`;
   return formattedDateTime;
};

const isFunction = (functionToCheck) => {
   return typeof functionToCheck === "function";
};

const tryCatchWrapper = (fn) => {
   return function (...args) {
      try {
         const result = fn(...args);

         if (result instanceof Promise) {
            return result.catch((error) => {
               console.error(`Error in ${fn.name}: `, error);
            });
         }
         return result;
      } catch (error) {
         console.error(`Error in ${fn.name}: `, error);
      }
   };
};

const convertToUndescoreSeparatedString = (str) => {
   if (str) {
      return str
         .replace(/( - Copy \(\d+\)|\(\d+\)).*/g, "") // remove (1), (2), etc. and - Copy (1), - Copy (2), etc.
         .trim() // remove leading and trailing spaces
         .replace(/[^A-Za-z0-9]/g, "_") // replace all non-alphanumeric characters with underscores
         .replace(/__/g, "_") // replace double underscores with single underscore
         .toLowerCase();
   }
   return "";
};

const addPhoneValidationToYup = (yup) => {
   const phoneUtil = PhoneNumberUtil.getInstance();

   yup.addMethod(Yup.string, "phone", function () {
      return this.test("phone", "Phone number is invalid", function (value) {
         const { path, createError } = this;

         if (!value) {
            return true; // if phonenumber is empty
         }

         //validate phonenumber
         try {
            const phoneNumber = phoneUtil.parseAndKeepRawInput(value);
            const isValid = phoneUtil.isValidNumber(phoneNumber);

            return isValid || createError({ path, message: "Phone number is invalid" });
         } catch (error) {
            return createError({ path, message: "Phone number is invalid" });
         }
      });
   });
};

const allowAction = (userId, created_by_id, userPermissions, permissionName) => {
   let owner = userId === created_by_id;
   let allowAccess = false;

   // Super user permissions
   if (userPermissions?.length && permissionName) {
      allowAccess = userPermissions.find((permission) => permission.name === permissionName)?.value;

      return allowAccess;
   }

   return owner;
};

const permissionChecker = (userPermissions, permissionName) => {
   return userPermissions?.find((userPermission) => userPermission.name === permissionName)?.value;
};

const delay = (ms) => {
   return new Promise((resolve) => setTimeout(resolve, ms));
};

const createFlowIntegrationValues = async (formIntegrations, integrations, newIntegrationsCallback, user, flowId) => {
   const flowIntegrations = [];

   const newIntegrations = Array.isArray(formIntegrations) ? formIntegrations.filter((integration) => !integrations.find((portalIntegration) => portalIntegration.id === integration)) : [];

   const existingIntegrations = Array.isArray(formIntegrations) ? formIntegrations.filter((integration) => integrations.find((portalIntegration) => portalIntegration.id === integration)) : [];

   existingIntegrations.forEach((integration) => flowIntegrations.push(integration));

   const handleCreateNewIntegrations = async () => {
      const results = await Promise.all(
         newIntegrations.map(async (integration) => {
            const input = {
               tenant_id: user.stripeId,
               integration_name: integration,
               flow_ids: [flowId],
               description: "",
               create_user_id: user.cxfUserId,
               modified_user_id: user.cxfUserId,
               current_version: "1",
               status: "active"
            };
            return Object.assign(await insertIntegrations(input), input);
         })
      );

      results.forEach((result) => {
         flowIntegrations.push(result[0].id);
         newIntegrationsCallback(result[0]);
      });
   };

   await tryCatchWrapper(handleCreateNewIntegrations)();

   return JSON.stringify(flowIntegrations);
};

const htmlHasContent = (string) => {
   const textOnly = string.replace(/<[^>]*>/g, "").trim();
   return textOnly.length > 0;
};

const findValueFromContext = (context, path) => {
   return path.split(".").reduce((acc, part) => acc && acc[part], context);
};

const removeNonIntegerCharsAndAcceptContext = (value) => {
   if (typeof value !== "string") {
      value = String(value);
   }

   if (value.trim().startsWith("@")) {
      return value;
   }

   const digits = value.match(/\d+/);
   return digits ? digits[0] : "";
};

const canParseJSON = (jsonString) => {
   try {
      JSON.parse(jsonString);
      return true;
   } catch (e) {
      return false;
   }
};

const USERS_RECENT_NODES_STORAGE_KEY = "usersRecentNodes";

const getStoredUsersRecentNodes = () => {
   try {
      const storedUsersRecentNodes = JSON.parse(localStorage.getItem(USERS_RECENT_NODES_STORAGE_KEY) ?? "{}");

      return storedUsersRecentNodes && typeof storedUsersRecentNodes === "object" && !Array.isArray(storedUsersRecentNodes) ? storedUsersRecentNodes : {};
   } catch {
      return {};
   }
};

const getStoredUserRecentNodes = (userId) => {
   if (!userId) return {};

   const usersRecentNodes = getStoredUsersRecentNodes();
   const userRecentNodes = usersRecentNodes[userId];

   return userRecentNodes && typeof userRecentNodes === "object" && !Array.isArray(userRecentNodes) ? userRecentNodes : {};
};

const setStoredUserRecentNodes = (userId, userRecentNodes) => {
   if (!userId) return;

   const usersRecentNodes = getStoredUsersRecentNodes();

   if (Object.keys(userRecentNodes).length) {
      usersRecentNodes[userId] = userRecentNodes;
   } else {
      delete usersRecentNodes[userId];
   }

   localStorage.setItem(USERS_RECENT_NODES_STORAGE_KEY, JSON.stringify(usersRecentNodes));
};

const urlWithTimestamp = (url) => {
   const separator = url.includes("?") ? "&" : "?";
   return `${url}${separator}t=${Date.now()}`;
};

const validateLogoSize = (value, logoUploadFile) => {
   let error;

   if (logoUploadFile && logoUploadFile?.size > getEnv("MAX_FILE_INPUT_SIZE")) {
      error = LOGO_SIZE_ERROR_MESSAGE;
   }

   return error;
};

const calculateTotalDuration = (data) => {
   const totalDuration = ((data?.reduce((sum, item) => sum + (item.duration ? item.duration : 0), 0) % 60000) / 1000)?.toFixed(3) ?? 0;

   return totalDuration;
};

const getCanvasCenter = (nodes) => {
   if (nodes.length === 0) return;

   let minX = Infinity;
   let maxX = -Infinity;
   let minY = Infinity;
   let maxY = -Infinity;

   for (const node of nodes) {
      const { x, y } = node.position;
      const nodeMaxX = x + (node.width || 0);
      const nodeMaxY = y + (node.height || 0);

      if (x < minX) minX = x;
      if (nodeMaxX > maxX) maxX = nodeMaxX;
      if (y < minY) minY = y;
      if (nodeMaxY > maxY) maxY = nodeMaxY;
   }

   return {
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2
   };
};

const getNodeIdToOpenFromModel = (layoutJson) => {
   if (!layoutJson || !layoutJson.layout?.children) return null;

   const tabsets = layoutJson.layout.children;

   for (const ts of tabsets) {
      if (ts.type !== "tabset") continue;

      const selectedIndex = ts.selected ?? 0;
      const selectedTab = ts.children?.[selectedIndex];

      if (!selectedTab) continue;

      if (selectedTab.id?.startsWith("componentProps-")) {
         return selectedTab.config?.nodeId || null;
      }
   }

   return null;
};

const combineStyles = (...stylesArray) => Object.assign({}, ...stylesArray);

const formatGroupName = (groupName) =>
   groupName
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

function uncompressValue(base64CompressedValue) {
   const compressedValue = Uint8Array.fromBase64(base64CompressedValue);

   if (compressedValue) return TEXT_DECODER.decode(snappyjs.uncompress(compressedValue));
   return null;
}

function uncompressRow(row) {
   for (const compressedColumnName of COMPRESSED_COLUMNS) {
      let uncompressedColumnValue = row?.[compressedColumnName];

      if (uncompressedColumnValue) {
         uncompressedColumnValue = uncompressValue(uncompressedColumnValue);
         if (uncompressedColumnValue && compressedColumnName !== "steps")
            try {
               uncompressedColumnValue = JSON.parse(uncompressedColumnValue);
            } catch (error) {
               console.error(`Unable to parse flow column ${compressedColumnName} as JSON object`, error);
            }
         row[compressedColumnName] = uncompressedColumnValue;
      }
   }
}

function uncompressRows(rows) {
   if (rows)
      if (Array.isArray(rows)) for (const row of rows) uncompressRow(row);
      else uncompressRow(rows);

   return rows;
}

function compressValue(uncompressedValue) {
   const compressedValue = new TextEncoder().encode(uncompressedValue);

   return snappyjs.compress(compressedValue).toBase64();
}

function compressFlow(flow) {
   let compressedFlow;

   compressedFlow = Object.assign({}, flow);

   for (const compressedColumnName of COMPRESSED_COLUMNS) {
      let uncompressedColumnValue = flow?.[compressedColumnName];

      if (uncompressedColumnValue) {
         if (typeof uncompressedColumnValue === "object")
            try {
               uncompressedColumnValue = JSON.stringify(uncompressedColumnValue);
            } catch (error) {
               console.error(`Unable to parse flow column ${compressedColumnName} as JSON object`, error);
            }
         compressedFlow[compressedColumnName] = compressValue(uncompressedColumnValue);
      }
   }

   return compressedFlow;
}

// Remove all execution-dependent (non-literal, dynamically generated) values in the context.
function cleanFlowContext(context) {
   let modified = false;

   const contextVariables = context?.variables,
      triggers = context?.triggers,
      contextSteps = context.steps,
      invocation = context?.invocation;

   if (contextVariables) {
      context.variables = {};
      modified = true;
   }

   if (triggers)
      Object.values(triggers).forEach((trigger) => {
         if (trigger?.credentials) trigger.credentials.value = "";

         if (trigger?.formData) trigger.formData = [];

         if (trigger?.payload) trigger.payload = "";

         if (trigger?.responseHeaders) trigger.responseHeaders = {};

         if (trigger?.responseCode) trigger.responseCode = "";

         if (trigger?.customResponseCode) trigger.customResponseCode = "";

         if (trigger?.respond)
            trigger.response = {
               node: "",
               value: ""
            };

         if (trigger?.topics) trigger.topics = [];

         if (trigger?.inputData) {
            trigger.inputData.body = {};
            trigger.inputData.params = {};
            trigger.inputData.headers = {};
         }

         if (trigger?.outputData) {
            trigger.outputData.body = {};
            trigger.outputData.headers = {};
         }

         modified = true;
      });

   if (contextSteps) {
      Object.values(contextSteps).forEach((contextStep) => {
         if (contextStep.inputData) contextStep.inputData = {};
         if (contextStep.outputData) contextStep.outputData = {};
         contextStep.status = "";
      });

      if (invocation) {
         context.invocation = {};
      }

      modified = true;
   }

   return modified;
}

/*
  Flow validation logic, to prevent corrupted flows from being saved to the database.
  Flow corruption may have many different root causes, including cxf-frontend bugs, concurrent flow modification from multiple browsers or accounts, network glitches, import of corrupted flow backup, etc.
*/

function validateAndCleanFlow(flowId, tenantId, flow) {
   /*
      Test for:
      - Valid JSON representation of flow steps
      - At least two nodes and one edge if flow is not new
      - No duplicate nodes, duplicate edges
      - Edges must connect existing nodes
      - Connectedness of all nodes via edges
      - Nodes must have non-empty IDs, edges must have non-empty source and target node IDs
      - Correct flow ID
      - Correct tenant ID
      - Flow context.steps must be an accurate reflection of the nodes and edges

      NOTE: flowId === null indicates a new flow with only Start and End nodes, whereas !== null indicates update of an existing flow.
  */

   function createReachabilityGraph(edgesMap) {
      const nodeTree = ["1"], // '1' is the ID of the start node.
         reachableNodes = new Set(nodeTree);

      let topOfStackNodeId, outgoingEdges;

      do {
         topOfStackNodeId = nodeTree.pop();
         outgoingEdges = edgesMap.get(topOfStackNodeId);
         if (outgoingEdges)
            outgoingEdges.forEach((nodeId) => {
               nodeTree.push(nodeId);
               reachableNodes.add(nodeId);
            });
      } while (nodeTree.length);

      return reachableNodes;
   }

   const embeddedFlowId = flow?.id ? flow.id.toString() : null;

   let steps,
      nodes,
      edges,
      context,
      connectedNodes,
      jsonSteps = false;

   if (!flow.steps) {
      console.error(`Flow ${flowId ?? "<new flow id>"} (${flow?.name}) for tenant ${tenantId} failed integrity validation. The flow steps are undefined or null.`);
      return 0;
   }

   if (typeof flow.steps === "string")
      try {
         steps = JSON.parse(flow.steps);
         jsonSteps = true;
      } catch (error) {
         console.error(`Flow ${flowId ?? "<new flow id>"} (${flow?.name}) for tenant ${tenantId} failed integrity validation. The flow steps are not a valid JSON object: ${JSON.stringify(error)}`);
         return 0;
      }
   else steps = flow.steps;
   context = steps?.context;

   if (flowId === "" || embeddedFlowId === "" || context?.flow_id === "") {
      console.error(`Flow ${flow?.name} for tenant ${tenantId} failed integrity validation. The flow ID, embedded flow id, and context.flow_id may not be an empty string.`);
      return 0;
   }
   if (tenantId === "" || flow?.tenant_id === "" || context?.tenant_id === "") {
      console.error(`Flow ${flow?.name} for tenant ${tenantId} failed integrity validation. The tenant ID, embedded tenant_id, and context.tenant_id may not be an empty string.`);
      return 0;
   }

   if (flowId) {
      if (embeddedFlowId && embeddedFlowId !== flowId) {
         console.error(`Flow ${flowId} (${flow?.name}) for tenant ${tenantId} failed integrity validation. The flow id ${embeddedFlowId} embedded in the flow does not match the expected flow ID ${flowId}.`);
         return 0;
      }
      if (context?.flow_id && context.flow_id !== flowId) {
         console.error(`Flow ${flowId} for tenant ${tenantId} failed integrity validation. The flow_id ${context.flow_id} embedded in the flow context does not match the expected flow ID ${flowId}.`);
         return 0;
      }
   }

   if (flow?.tenant_id && flow.tenant_id !== tenantId) {
      console.error(
         `Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. The tenant_id ${flow.tenant_id} embedded in the flow does not match the tenant_id ${tenantId} of the account holder.`
      );
      return 0;
   }
   if (context?.tenant_id && context.tenant_id !== tenantId) {
      console.error(
         `Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. The tenant_id ${context.tenant_id} embedded in the flow context does not match the tenant_id ${tenantId} of the account holder.`
      );
      return 0;
   }

   if (flowId && cleanFlowContext(context) && jsonSteps) flow.steps = JSON.stringify(steps);

   nodes = steps.nodes;
   edges = steps.edges;
   if (nodes && edges) {
      const nodesSet = new Set(),
         edgesMap = new Map();

      if (nodes?.length > 1 && edges?.length) {
         if (
            !nodes.every((node) => {
               if (node?.id) {
                  if (node.id.includes("undefined")) {
                     console.error(`Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. At least one node has an invalid "id" property ${node.id}.`);
                     return false;
                  }

                  if (nodesSet.has(node.id)) {
                     console.error(`Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. Duplicate flow node ${node.id}.`);
                     return false;
                  }
                  nodesSet.add(node.id);
                  return true;
               } else {
                  console.error(`Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. At least one node has no valid "id" property.`);
                  return false;
               }
            })
         )
            return 0;

         if (
            !edges.every((edge) => {
               if (nodesSet.has(edge.source) && nodesSet.has(edge.target)) {
                  const childNodes = edgesMap.get(edge.source);

                  if (edge?.source && edge?.target)
                     if (childNodes)
                        if (childNodes.has(edge.target)) {
                           console.error(`Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. Duplicate edge (${edge.source}, ${edge.target}).`);
                           return false;
                        } else childNodes.add(edge.target);
                     else edgesMap.set(edge.source, new Set([edge.target]));
                  else {
                     console.error(`Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. At least one edge has no valid source or target "id".`);
                     return false;
                  }
                  return true;
               } else {
                  console.error(`Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. Flow edge ${JSON.stringify(edge)} references a non-existing source or target node.`);
                  return false;
               }
            })
         )
            return 0;

         connectedNodes = createReachabilityGraph(edgesMap);
         if (connectedNodes.size < nodesSet.size) {
            console.error(
               `Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. Flow nodes ${JSON.stringify(Array.from(nodesSet.difference(connectedNodes)))} are not connected by edges directly or indirectly with the Start node.`
            );
            return 0;
         }

         const nodeComponentKeys = new Set(),
            contextComponentKeys = new Set(Object.keys(context.steps));

         const hasOnlyValidComponentKeys = nodes.every((node) => {
            const componentKey = node?.data?.componentKey;

            if (componentKey)
               if (componentKey.includes("undefined")) {
                  console.error(`Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. A flow node ${JSON.stringify(node)} contains an invalid componentKey ${componentKey}.`);
                  return false;
               } else nodeComponentKeys.add(componentKey);

            return true;
         });
         if (!hasOnlyValidComponentKeys) return 0;

         const missingNodesInContext = nodeComponentKeys.difference(contextComponentKeys),
            invalidNodesInContext = contextComponentKeys.difference(nodeComponentKeys);

         if (missingNodesInContext.size > 0) {
            console.error(
               `Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. The flow context representation is corrupted. One or more flow nodes is missing in context.steps: ${[...missingNodesInContext]}`
            );
            return 0;
         }

         if (invalidNodesInContext.size > 0) {
            console.warn(
               `Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. The flow context representation is corrupted. One or more context nodes are invalid: ${[...invalidNodesInContext]}`
            );
            return 1;
         }
      } else if (flowId !== null) {
         console.error(
            `Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. The flow must contain at least two nodes (Start, End) and one edge but contains ${nodes?.length} nodes and ${edges?.length} edges.`
         );
         return 0;
      }
   } else {
      console.error(`Flow ${flowId ?? "<new flow id>"} for tenant ${tenantId} failed integrity validation. The flow steps do not contain "nodes" and / or "edges".`);
      return 0;
   }

   return 2;
}

const hideComponentData = (contextCopy) => {
   let steps = contextCopy.steps;

   Object.keys(steps).forEach((stepKey) => {
      const step = steps[stepKey];
      if (Object.prototype.hasOwnProperty.call(step, "componentData")) {
         delete step.componentData;
      }
      steps[stepKey] = step;
   });
   contextCopy.steps = steps;

   return contextCopy;
};

export {
   ConditionalWrapper,
   exportToJson,
   getCookie,
   getLocalDateTimeString,
   getLocalTimeString,
   removeDuplicateObjects,
   setCookie,
   uploadFile,
   isEmptyValue,
   isArrayofObjects,
   removeProperty,
   getObjectPaths,
   deepEqual,
   formatDateAndTime,
   stringifyWithSpaces,
   isFunction,
   isValidJsonString,
   tryCatchWrapper,
   convertToUndescoreSeparatedString,
   addPhoneValidationToYup,
   allowAction,
   delay,
   htmlHasContent,
   createFlowIntegrationValues,
   findValueFromContext,
   removeNonIntegerCharsAndAcceptContext,
   canParseJSON,
   getSchemaUrlFromConfigFiles,
   urlWithTimestamp,
   validateLogoSize,
   calculateTotalDuration,
   permissionChecker,
   getCanvasCenter,
   getNodeIdToOpenFromModel,
   getStoredUserRecentNodes,
   combineStyles,
   formatGroupName,
   setStoredUserRecentNodes,
   uncompressRows,
   compressFlow,
   validateAndCleanFlow,
   cleanFlowContext,
   hideComponentData
};
